import { useState, type FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { buildGoogleAuthUrl, buildGitHubAuthUrl } from "../auth/oauth-config";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || "/";

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await signIn(email, password);
      navigate(from, { replace: true });
    } catch {
      setError("Invalid email or password");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark-950 p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">VulnTask</h1>
          <p className="text-dark-400 text-sm">Sign in to manage your tasks</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-dark-800 rounded-lg border border-dark-600 p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-900/30 border border-red-700 rounded text-red-300 text-sm">
              {error}
            </div>
          )}

          <div className="mb-4">
            <label htmlFor="email" className="block text-sm text-dark-300 mb-1">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="w-full px-3 py-2 bg-dark-900 border border-dark-600 rounded text-white placeholder-dark-500 focus:outline-none focus:border-blue-500"
              required
            />
          </div>

          <div className="mb-6">
            <label htmlFor="password" className="block text-sm text-dark-300 mb-1">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              className="w-full px-3 py-2 bg-dark-900 border border-dark-600 rounded text-white placeholder-dark-500 focus:outline-none focus:border-blue-500"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>

          <div className="mt-4 flex flex-col gap-2">
            <a
              href={buildGoogleAuthUrl()}
              className="flex items-center justify-center gap-2 w-full py-2 bg-dark-700 text-dark-200 rounded hover:bg-dark-600 text-sm"
            >
              Continue with Google
            </a>
            <a
              href={buildGitHubAuthUrl()}
              className="flex items-center justify-center gap-2 w-full py-2 bg-dark-700 text-dark-200 rounded hover:bg-dark-600 text-sm"
            >
              Continue with GitHub
            </a>
          </div>
        </form>

        <p className="text-center text-dark-500 text-xs mt-4">
          Demo app — use any credentials to test
        </p>
      </div>
    </div>
  );
}
